/**
 * /admin/access — Owner-only Access console for ZenCricketAnalyzer.
 *
 * Two sub-sections (tabs):
 *   1. Analyzer access — per-user, per-tournament analyzer grants
 *      (the original purpose of this page).
 *   2. Auction access — admin shares a fully-set-up LiveAuction with a
 *      user; the backend clones the auction state into the grantee's
 *      account so they get an independent copy to run their bid against.
 *
 * Backend:
 *   GET    /api/admin/v2/analyzer-grants/...
 *   GET    /api/admin/v2/live-auction-grants/...
 */
import { useEffect, useMemo, useState } from "react";
import {
  Search, Plus, RefreshCw, X, Check, ShieldOff, ShieldCheck, AlertCircle,
  Gavel, Users as UsersIcon, Trash2, UserPlus, Copy, KeyRound, ClipboardList,
} from "lucide-react";
import {
  listAnalyzerGrants,
  createAnalyzerGrant,
  updateAnalyzerGrant,
  revokeAnalyzerGrant,
  restoreAnalyzerGrant,
  searchAnalyzerGrantUsers,
  listAnalyzerGrantCompetitions,
  createAnalyzerUser,
  listLiveAuctionTemplates,
  listLiveAuctionGrants,
  createLiveAuctionGrant,
  deleteLiveAuctionGrant,
  searchLiveAuctionGrantUsers,
  listPreAuctions,
  getPreAuctionGrants,
  addPreAuctionGrant,
  removePreAuctionGrant,
  searchPreAuctionGrantUsers,
} from "../../api/owner.api";

const STATUS_FILTERS = [
  { key: "",      label: "All" },
  { key: "true",  label: "Active" },
  { key: "false", label: "Revoked" },
];

export default function AdminAccess() {
  const [tab, setTab] = useState(() => {
    const u = new URL(window.location.href);
    const q = u.searchParams.get("tab");
    // 2026-06-30: "auction" → renamed to "live-auction" (sub-tab key).
    // Old bookmarks keep working via the alias.
    if (q === "live-auction" || q === "auction") return "live-auction";
    if (q === "pre-auction") return "pre-auction";
    return "analyzer";
  });

  const setTabPersisted = (next) => {
    setTab(next);
    const u = new URL(window.location.href);
    u.searchParams.set("tab", next);
    window.history.replaceState({}, "", u.toString());
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b border-border-default">
        <TabButton
          active={tab === "analyzer"}
          onClick={() => setTabPersisted("analyzer")}
          icon={<ShieldCheck className="w-4 h-4" />}
        >Analyzer Access</TabButton>
        <TabButton
          active={tab === "live-auction"}
          onClick={() => setTabPersisted("live-auction")}
          icon={<Gavel className="w-4 h-4" />}
        >Live Auction Access</TabButton>
        <TabButton
          active={tab === "pre-auction"}
          onClick={() => setTabPersisted("pre-auction")}
          icon={<ClipboardList className="w-4 h-4" />}
        >Pre-Auction Access</TabButton>
      </div>
      {tab === "analyzer" && <AnalyzerAccessSection />}
      {tab === "live-auction" && <AuctionAccessSection />}
      {tab === "pre-auction" && <PreAuctionAccessSection />}
    </div>
  );
}

function TabButton({ active, onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 -mb-px text-sm font-medium inline-flex items-center gap-2 border-b-2 transition-colors ${
        active
          ? "border-button-primary text-text-primary"
          : "border-transparent text-text-secondary hover:text-text-primary"
      }`}
    >
      {icon}{children}
    </button>
  );
}

function AnalyzerAccessSection() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerGrant, setDrawerGrant] = useState(null); // null = new grant
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(fetchGrants, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  async function fetchGrants() {
    setLoading(true);
    try {
      const res = await listAnalyzerGrants({
        search: search || undefined,
        is_active: statusFilter || undefined,
      });
      setRows(res.data.results || []);
      setCount(res.data.count || 0);
    } finally {
      setLoading(false);
    }
  }

  const openGrant = (g) => { setDrawerGrant(g); setCreating(false); setDrawerOpen(true); };
  const openNew = () => { setDrawerGrant(null); setCreating(true); setDrawerOpen(true); };
  const close = (refresh) => {
    setDrawerOpen(false);
    setDrawerGrant(null);
    setCreating(false);
    if (refresh) fetchGrants();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Analyzer access</h1>
          <p className="text-sm text-text-secondary mt-1">
            Per-user, per-tournament grants for ZenCricketAnalyzer. Signed analysts see only the tournaments listed on their grant.
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-button-primary text-white rounded-md hover:opacity-90 text-sm"
        >
          <Plus className="w-4 h-4" /> New grant
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            className="w-full pl-9 pr-3 py-2 bg-bg-secondary border border-border-default rounded text-sm"
          />
        </div>
        <div className="flex gap-1">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              className={`px-3 py-1.5 text-xs rounded border ${
                statusFilter === s.key
                  ? "bg-button-primary/20 text-button-primary border-button-primary/40"
                  : "bg-bg-secondary text-text-secondary border-border-default hover:text-text-primary"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button onClick={fetchGrants} className="text-text-secondary hover:text-text-primary" title="Refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
        <div className="text-xs text-text-secondary ml-auto">
          {count} grant{count === 1 ? "" : "s"}
        </div>
      </div>

      {loading ? (
        <div className="bg-bg-secondary border border-border-default rounded-lg p-6 text-center text-text-secondary text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-bg-secondary border border-border-default rounded-lg p-8 text-center">
          <ShieldOff className="w-10 h-10 text-text-secondary mx-auto mb-3" />
          <p className="text-text-primary font-semibold">No grants yet.</p>
          <p className="text-text-secondary text-sm mt-1">Issue the first one with “New grant”.</p>
        </div>
      ) : (
        <GrantsTable rows={rows} onRowClick={openGrant} />
      )}

      {drawerOpen && (
        <GrantDrawer
          grant={drawerGrant}
          creating={creating}
          onClose={close}
        />
      )}
    </div>
  );
}

function GrantsTable({ rows, onRowClick }) {
  return (
    <div className="bg-bg-secondary border border-border-default rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-bg-primary/50 text-text-secondary text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">User</th>
            <th className="text-left px-3 py-2">Tournaments</th>
            <th className="text-center px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Granted at</th>
            <th className="text-left px-3 py-2">By</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((g) => (
            <tr
              key={g.id}
              onClick={() => onRowClick(g)}
              className="border-t border-border-default hover:bg-bg-primary/40 cursor-pointer"
            >
              <td className="px-3 py-2">
                <div className="text-text-primary font-medium">{g.user_detail?.full_name || g.user_detail?.email}</div>
                <div className="text-text-secondary text-xs">{g.user_detail?.email}</div>
              </td>
              <td className="px-3 py-2">
                {g.competitions_detail?.length === 0 ? (
                  <span className="text-amber-400 text-xs">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Empty — no data accessible
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {g.competitions_detail?.slice(0, 4).map((c) => (
                      <span key={c.id} className="bg-bg-primary px-2 py-0.5 rounded text-xs text-text-secondary">
                        {c.name}{c.season ? ` · ${c.season}` : ""}
                      </span>
                    ))}
                    {g.competitions_detail?.length > 4 && (
                      <span className="text-text-secondary text-xs">+{g.competitions_detail.length - 4} more</span>
                    )}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-center">
                {g.is_active ? (
                  <span className="inline-flex items-center gap-1 text-emerald-400 text-xs">
                    <ShieldCheck className="w-3 h-3" /> Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-rose-400 text-xs">
                    <ShieldOff className="w-3 h-3" /> Revoked
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-text-secondary text-xs">{fmtDate(g.granted_at)}</td>
              <td className="px-3 py-2 text-text-secondary text-xs">{g.granted_by_detail?.email || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GrantDrawer({ grant, creating, onClose }) {
  // Existing grant → load user from row; creating → user picker enabled.
  const [user, setUser] = useState(creating ? null : grant?.user_detail);
  const [comps, setComps] = useState(grant?.competitions_detail || []);
  const [notes, setNotes] = useState(grant?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Set when the admin mints a brand-new user from inside the picker.
  // The plaintext password is shown ONCE in a sticky banner until close.
  const [mintedCreds, setMintedCreds] = useState(null);

  async function save() {
    if (!user) { setError("Pick a user first."); return; }
    setSaving(true); setError(null);
    try {
      if (creating) {
        await createAnalyzerGrant({
          userId: user.id,
          competitionIds: comps.map((c) => c.id),
          notes,
        });
      } else {
        await updateAnalyzerGrant(grant.id, {
          competitionIds: comps.map((c) => c.id),
          notes,
        });
      }
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive() {
    if (!grant) return;
    setSaving(true); setError(null);
    try {
      if (grant.is_active) await revokeAnalyzerGrant(grant.id);
      else await restoreAnalyzerGrant(grant.id);
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={() => onClose(false)} />
      {/* Drawer is a flex column: header (shrink) + scrollable body
          (flex-1) + sticky footer with action buttons (shrink). The
          tournament picker's absolute-positioned dropdown lives
          inside the scroll body so it can no longer overlay the
          Create-grant / Save button — the button sits in a separate
          flex region pinned to the bottom of the drawer. */}
      <aside className="relative w-full max-w-md bg-bg-primary border-l border-border-default flex flex-col">
        <div className="shrink-0 p-4 flex items-center justify-between border-b border-border-default">
          <h2 className="text-lg font-semibold text-text-primary">
            {creating ? "New grant" : "Edit grant"}
          </h2>
          <button onClick={() => onClose(false)} className="text-text-secondary hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {mintedCreds && (
            <MintedCredentialsBanner
              creds={mintedCreds}
              onDismiss={() => setMintedCreds(null)}
            />
          )}

          <Section label="User">
            {creating ? (
              <UserPicker
                value={user}
                onChange={setUser}
                onMinted={(payload) => {
                  setUser(payload.user);
                  setMintedCreds(payload);
                }}
              />
            ) : (
              <div className="bg-bg-secondary border border-border-default rounded px-3 py-2">
                <div className="text-text-primary font-medium">{user?.full_name || user?.email}</div>
                <div className="text-text-secondary text-xs">{user?.email}</div>
              </div>
            )}
          </Section>

          <Section label={`Tournaments (${comps.length})`}>
            <CompetitionPicker value={comps} onChange={setComps} />
            <p className="text-text-secondary text-xs mt-1">
              Analyst will see data only for these tournaments. Empty list = no access.
            </p>
          </Section>

          <Section label="Notes (internal)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Contract reference, expiry…"
              className="w-full bg-bg-secondary border border-border-default rounded px-3 py-2 text-sm"
            />
          </Section>

          {error && (
            <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded px-3 py-2 text-xs">
              {error}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-border-default bg-bg-primary p-4 flex gap-2">
          <button
            onClick={save}
            disabled={saving || !user}
            className="flex-1 px-3 py-2 bg-button-primary text-white rounded text-sm hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : creating ? "Create grant" : "Save changes"}
          </button>
          {!creating && grant && (
            <button
              onClick={toggleActive}
              disabled={saving}
              className={`px-3 py-2 rounded text-sm border ${grant.is_active
                ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}
            >
              {grant.is_active ? "Revoke" : "Restore"}
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function UserPicker({ value, onChange, onMinted }) {
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState(""); // "" / "B2C" / "B2B" / "STAFF"
  const [allUsers, setAllUsers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, shown: 0 });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  // Toggled when the admin clicks "+ Create new user" inside the dropdown.
  const [mintOpen, setMintOpen] = useState(false);

  // Fetch the full user list on first open and cache for the session.
  // Backend returns all active users (B2C + B2B) when q is empty.
  useEffect(() => {
    if (!open || loadedOnce) return;
    setLoading(true);
    searchAnalyzerGrantUsers("")
      .then((res) => {
        setAllUsers(res.data.results || []);
        setMeta({ total: res.data.total || 0, shown: res.data.shown || 0 });
        setLoadedOnce(true);
      })
      .finally(() => setLoading(false));
  }, [open, loadedOnce]);

  // Client-side filter — no extra network call per keystroke. Segment
  // filter narrows first, then the text search runs across what's left.
  const filtered = useMemo(() => {
    let rows = allUsers;
    if (seg) rows = rows.filter((u) => (u.account_type || "") === seg);
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((u) =>
      (u.email || "").toLowerCase().includes(t)
      || (u.username || "").toLowerCase().includes(t)
      || (u.full_name || "").toLowerCase().includes(t)
      || (u.organization_name || "").toLowerCase().includes(t)
    );
  }, [allUsers, q, seg]);

  // Per-segment counts for the filter pills.
  const counts = useMemo(() => {
    const c = { ALL: allUsers.length, B2C: 0, B2B: 0, STAFF: 0 };
    for (const u of allUsers) {
      const k = u.account_type || "B2C";
      if (k in c) c[k] += 1;
    }
    return c;
  }, [allUsers]);

  if (value) {
    return (
      <div className="flex items-center justify-between bg-bg-secondary border border-border-default rounded px-3 py-2">
        <div>
          <div className="text-text-primary font-medium">{value.full_name || value.email}</div>
          <div className="text-text-secondary text-xs">{value.email}</div>
        </div>
        <button onClick={() => { onChange(null); setQ(""); setOpen(false); }} className="text-text-secondary hover:text-text-primary">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (mintOpen) {
    return (
      <CreateUserInline
        onCancel={() => setMintOpen(false)}
        onCreated={(payload) => {
          // Refresh the cached user list so the next picker open includes
          // the new account (and the "grant" tag updates correctly).
          setLoadedOnce(false);
          setMintOpen(false);
          setOpen(false);
          onMinted?.(payload);
        }}
      />
    );
  }

  return (
    <div className="relative">
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={loadedOnce ? `Filter ${meta.shown} users…` : "Click to load users…"}
        className="w-full bg-bg-secondary border border-border-default rounded px-3 py-2 text-sm"
      />
      {onMinted && (
        <button
          type="button"
          onClick={() => { setMintOpen(true); setOpen(false); }}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-button-primary hover:underline"
        >
          <UserPlus className="w-3.5 h-3.5" /> Create new user
        </button>
      )}
      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-bg-secondary border border-border-default rounded shadow-xl z-10 max-h-96 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-text-secondary text-xs">Loading users…</div>}
          {!loading && loadedOnce && (
            <div className="sticky top-0 bg-bg-secondary border-b border-border-default/60 z-10">
              <div className="px-3 py-2 flex flex-wrap items-center gap-1.5">
                <SegmentPill active={seg === ""}     onClick={() => setSeg("")}      label="All"   count={counts.ALL}   tone="slate" />
                <SegmentPill active={seg === "B2C"}   onClick={() => setSeg("B2C")}   label="B2C"   count={counts.B2C}   tone="emerald" />
                <SegmentPill active={seg === "B2B"}   onClick={() => setSeg("B2B")}   label="B2B"   count={counts.B2B}   tone="sky" />
                <SegmentPill active={seg === "STAFF"} onClick={() => setSeg("STAFF")} label="Staff" count={counts.STAFF} tone="indigo" />
              </div>
              <div className="px-3 pb-1.5 text-[10px] uppercase tracking-wider text-text-secondary">
                {q.trim()
                  ? `${filtered.length} match${filtered.length === 1 ? "" : "es"}`
                  : `${filtered.length} user${filtered.length === 1 ? "" : "s"}${meta.total > meta.shown ? ` (showing ${meta.shown} of ${meta.total})` : ""}`}
              </div>
            </div>
          )}
          {!loading && loadedOnce && filtered.length === 0 && (
            <div className="px-3 py-3 text-text-secondary text-xs">No matches.</div>
          )}
          {filtered.map((u) => (
            <button
              key={u.id}
              onClick={() => { onChange(u); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 hover:bg-bg-primary/60 border-b border-border-default/50 last:border-b-0"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-text-primary text-sm truncate">{u.full_name || u.email}</div>
                  <div className="text-text-secondary text-xs truncate">
                    {u.email}
                    {u.organization_name && <span className="text-text-muted"> · {u.organization_name}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <AccountTypeTag type={u.account_type} />
                  {u.has_active_grant && (
                    <span className="text-emerald-300 text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-400/30">grant</span>
                  )}
                  {u.has_revoked_grant && !u.has_active_grant && (
                    <span className="text-amber-300 text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-400/30">revoked</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateUserInline({ onCancel, onCreated }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e?.preventDefault?.();
    if (!email.trim()) { setError("Email is required."); return; }
    setBusy(true); setError(null);
    try {
      const res = await createAnalyzerUser({
        email: email.trim(),
        fullName: fullName.trim(),
        username: username.trim(),
        password: password.trim(),
      });
      onCreated({
        user: res.data.user,
        plaintext_password: res.data.plaintext_password,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.error
        || err?.response?.data?.detail
        || err.message;
      setError(detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-2 bg-bg-secondary border border-border-default rounded p-3">
      <div className="text-xs uppercase tracking-wider text-text-secondary font-semibold flex items-center gap-1.5">
        <UserPlus className="w-3.5 h-3.5" /> Create new analyst login
      </div>
      <input
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email (required) — e.g. chepauk@tnpl.zencricketanalyzer.com"
        className="w-full bg-bg-primary border border-border-default rounded px-3 py-2 text-sm"
      />
      <input
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        placeholder="Display name (optional) — e.g. Chepauk Super Gillies"
        className="w-full bg-bg-primary border border-border-default rounded px-3 py-2 text-sm"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Username (auto from email if blank)"
          className="bg-bg-primary border border-border-default rounded px-3 py-2 text-sm"
        />
        <input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password (auto if blank, min 8)"
          className="bg-bg-primary border border-border-default rounded px-3 py-2 text-sm font-mono"
        />
      </div>
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded px-2.5 py-1.5 text-xs flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span className="break-all">{error}</span>
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={busy}
          className="flex-1 px-3 py-2 bg-button-primary text-white rounded text-sm hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create user"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-2 text-sm border border-border-default rounded text-text-secondary hover:text-text-primary"
        >
          Cancel
        </button>
      </div>
      <p className="text-text-secondary text-[10.5px] leading-snug pt-0.5">
        The password is shown ONCE in a copyable banner once the user is created — copy it before closing the drawer.
      </p>
    </form>
  );
}

function MintedCredentialsBanner({ creds, onDismiss }) {
  const [copied, setCopied] = useState("");

  async function copy(label, text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      // clipboard rejected; admin can still hand-copy.
    }
  }

  const { user, plaintext_password: pw } = creds;
  const sheet = `Username: ${user.username}\nEmail: ${user.email}\nPassword: ${pw}`;

  return (
    <div className="bg-emerald-500/10 border border-emerald-500/40 rounded p-3 text-xs space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="text-emerald-200 font-semibold flex items-center gap-1.5">
          <KeyRound className="w-3.5 h-3.5" /> Login created — copy now, password won't reappear
        </div>
        <button onClick={onDismiss} className="text-emerald-200/70 hover:text-emerald-100 shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <CredentialRow label="Username" value={user.username} copied={copied} onCopy={copy} />
      <CredentialRow label="Email"    value={user.email}    copied={copied} onCopy={copy} />
      <CredentialRow label="Password" value={pw}            copied={copied} onCopy={copy} mono />
      <button
        type="button"
        onClick={() => copy("All", sheet)}
        className="w-full mt-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 rounded text-emerald-100 text-[11px] font-medium"
      >
        <Copy className="w-3 h-3" /> {copied === "All" ? "Copied all 3 lines" : "Copy all 3 lines"}
      </button>
    </div>
  );
}

function CredentialRow({ label, value, copied, onCopy, mono }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-emerald-200/70 uppercase tracking-wider text-[10px] w-16 shrink-0">{label}</div>
      <code className={`flex-1 bg-bg-primary/60 border border-emerald-500/20 rounded px-2 py-1 ${mono ? "font-mono" : ""} text-emerald-100 break-all`}>
        {value}
      </code>
      <button
        type="button"
        onClick={() => onCopy(label, value)}
        className="text-emerald-200/70 hover:text-emerald-100 p-1 shrink-0"
        title={`Copy ${label}`}
      >
        {copied === label
          ? <Check className="w-3.5 h-3.5 text-emerald-300" />
          : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function CompetitionPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const selectedIds = useMemo(() => new Set(value.map((c) => c.id)), [value]);

  useEffect(() => {
    const t = setTimeout(() => {
      listAnalyzerGrantCompetitions(q.trim())
        .then((res) => setResults(res.data.results || []));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const toggle = (c) => {
    if (selectedIds.has(c.id)) onChange(value.filter((x) => x.id !== c.id));
    else onChange([...value, c]);
  };

  return (
    <div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {value.map((c) => (
            <button
              key={c.id}
              onClick={() => toggle(c)}
              className="bg-button-primary/20 text-button-primary border border-button-primary/40 rounded px-2 py-0.5 text-xs flex items-center gap-1 hover:bg-button-primary/30"
            >
              {c.name}{c.season ? ` · ${c.season}` : ""}
              <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search tournaments…"
          className="w-full bg-bg-secondary border border-border-default rounded px-3 py-2 text-sm"
        />
        {open && (
          <div className="absolute left-0 right-0 mt-1 bg-bg-secondary border border-border-default rounded shadow-xl z-10 max-h-64 overflow-y-auto">
            {results.length === 0 && <div className="px-3 py-2 text-text-secondary text-xs">No tournaments.</div>}
            {results.map((c) => {
              const picked = selectedIds.has(c.id);
              const matchCount = c.match_count ?? null;
              const empty = matchCount === 0;
              return (
                <button
                  key={c.id}
                  onClick={() => toggle(c)}
                  title={empty
                    ? "0 matches — this is an empty placeholder competition, granting access here shows the analyst nothing."
                    : ""}
                  className={`w-full text-left px-3 py-2 hover:bg-bg-primary/60 border-b border-border-default/50 last:border-b-0 flex items-center gap-2 ${picked ? "bg-button-primary/10" : ""} ${empty ? "opacity-60" : ""}`}
                >
                  <Check className={`w-4 h-4 ${picked ? "text-button-primary" : "text-transparent"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-text-primary text-sm truncate flex items-center gap-1.5">
                      {c.name}
                      {empty && (
                        <span className="text-amber-300 text-[10px] uppercase tracking-wider px-1 rounded bg-amber-500/15 border border-amber-400/40">
                          empty
                        </span>
                      )}
                    </div>
                    <div className="text-text-secondary text-xs flex items-center gap-2">
                      {c.season && <span>Season {c.season}</span>}
                      {matchCount !== null && (
                        <span className={empty ? "text-amber-300" : "text-emerald-300"}>
                          {matchCount} match{matchCount === 1 ? "" : "es"}
                        </span>
                      )}
                      {c.data_source && (
                        <span className="text-text-muted uppercase text-[9.5px] tracking-wider">{c.data_source}</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

const SEGMENT_TONES = {
  slate:   { active: "bg-slate-500/25 border-slate-400/60 text-slate-100",       idle: "border-border-default text-text-secondary hover:text-text-primary" },
  emerald: { active: "bg-emerald-500/25 border-emerald-400/60 text-emerald-100", idle: "border-border-default text-text-secondary hover:text-text-primary" },
  sky:     { active: "bg-sky-500/25 border-sky-400/60 text-sky-100",             idle: "border-border-default text-text-secondary hover:text-text-primary" },
  indigo:  { active: "bg-indigo-500/25 border-indigo-400/60 text-indigo-100",    idle: "border-border-default text-text-secondary hover:text-text-primary" },
};

function SegmentPill({ active, onClick, label, count, tone = "slate" }) {
  const t = SEGMENT_TONES[tone] || SEGMENT_TONES.slate;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${active ? t.active : t.idle}`}
    >
      {label}
      <span className={`tabular-nums ${active ? "opacity-90" : "opacity-60"}`}>{count}</span>
    </button>
  );
}

const ACCOUNT_TYPE_TAGS = {
  B2C:    { label: "B2C",    cls: "text-emerald-300 bg-emerald-500/15 border-emerald-400/30" },
  B2B:    { label: "B2B",    cls: "text-sky-300 bg-sky-500/15 border-sky-400/30" },
  STAFF:  { label: "Staff",  cls: "text-indigo-300 bg-indigo-500/15 border-indigo-400/30" },
  PLAYER: { label: "Player", cls: "text-amber-300 bg-amber-500/15 border-amber-400/30" },
};

function AccountTypeTag({ type }) {
  const def = ACCOUNT_TYPE_TAGS[type];
  if (!def) return null;
  return (
    <span className={`text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${def.cls}`}>
      {def.label}
    </span>
  );
}

/* ─── Auction Access section ─────────────────────────────────────────
   List every LiveAuction admin (superuser) has set up. Click one to
   open a drawer that shows existing grants + lets you add a new
   grantee. Each grant deep-clones the auction state into the grantee's
   account so their copy is independent. */

function AuctionAccessSection() {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openTemplateId, setOpenTemplateId] = useState(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await listLiveAuctionTemplates(search);
      setTemplates(res.data.templates || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchTemplates, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openTemplate = templates.find((t) => t.id === openTemplateId);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Live Auction access</h1>
          <p className="text-sm text-text-secondary mt-1">
            Share a pre-set LiveAuction with a user. They get an independent clone in their analyzer — sales, unsold and the "my team" pick stay scoped to that user.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by auction name…"
            className="w-full pl-9 pr-3 py-2 bg-bg-secondary border border-border-default rounded text-sm"
          />
        </div>
        <button onClick={fetchTemplates} className="text-text-secondary hover:text-text-primary" title="Refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
        <div className="text-xs text-text-secondary ml-auto">
          {templates.length} auction{templates.length === 1 ? "" : "s"}
        </div>
      </div>

      {loading && templates.length === 0 ? (
        <div className="bg-bg-secondary border border-border-default rounded-lg p-6 text-center text-text-secondary text-sm">Loading…</div>
      ) : templates.length === 0 ? (
        <div className="bg-bg-secondary border border-border-default rounded-lg p-8 text-center">
          <Gavel className="w-10 h-10 text-text-secondary mx-auto mb-3" />
          <p className="text-text-primary font-semibold">No admin auctions yet.</p>
          <p className="text-text-secondary text-sm mt-1">
            Create an auction inside the analyzer SPA (Manivannan's account) and it'll appear here for sharing.
          </p>
        </div>
      ) : (
        <TemplatesTable templates={templates} onPick={setOpenTemplateId} />
      )}

      {openTemplate && (
        <AuctionGrantsDrawer
          template={openTemplate}
          onClose={(refresh) => {
            setOpenTemplateId(null);
            if (refresh) fetchTemplates();
          }}
        />
      )}
    </div>
  );
}

function TemplatesTable({ templates, onPick }) {
  return (
    <div className="bg-bg-secondary border border-border-default rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-bg-primary/40 text-text-secondary text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-4 py-2.5 font-semibold">Auction</th>
            <th className="text-left px-4 py-2.5 font-semibold">Owner</th>
            <th className="text-right px-4 py-2.5 font-semibold">Teams</th>
            <th className="text-right px-4 py-2.5 font-semibold">Sets</th>
            <th className="text-right px-4 py-2.5 font-semibold">Granted to</th>
            <th className="text-left px-4 py-2.5 font-semibold">Updated</th>
            <th className="px-4 py-2.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-default">
          {templates.map((t) => (
            <tr key={t.id} className="hover:bg-bg-primary/30 cursor-pointer" onClick={() => onPick(t.id)}>
              <td className="px-4 py-3">
                <div className="text-text-primary font-medium">{t.name}</div>
                {t.season && <div className="text-text-secondary text-xs">{t.season}</div>}
              </td>
              <td className="px-4 py-3 text-text-secondary text-xs">{t.owner?.full_name || t.owner?.email}</td>
              <td className="px-4 py-3 text-right tabular-nums">{t.teams_count}</td>
              <td className="px-4 py-3 text-right tabular-nums">{t.sets_count}</td>
              <td className="px-4 py-3 text-right tabular-nums">
                <span className={t.grants_count > 0 ? "text-emerald-300 font-semibold" : "text-text-secondary"}>
                  {t.grants_count} {t.grants_count === 1 ? "user" : "users"}
                </span>
              </td>
              <td className="px-4 py-3 text-text-secondary text-xs">{fmtDate(t.updated_at)}</td>
              <td className="px-4 py-3 text-right">
                <button className="text-button-primary text-xs hover:underline">Manage →</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuctionGrantsDrawer({ template, onClose }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);

  const fetchGrants = async () => {
    setLoading(true);
    try {
      const res = await listLiveAuctionGrants({ template: template.id });
      const list = res.data.results || res.data || [];
      setGrants(Array.isArray(list) ? list : (list.results || []));
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGrants(); /* eslint-disable-next-line */ }, [template.id]);

  const grant = async (user) => {
    setError(null);
    try {
      await createLiveAuctionGrant({ templateAuctionId: template.id, userId: user.id });
      setTouched(true);
      setPickerOpen(false);
      await fetchGrants();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.detail || err.message);
    }
  };

  const revoke = async (g) => {
    if (!confirm(`Revoke ${g.user_detail?.full_name || g.user_detail?.email}'s access? Their cloned auction stays in their account but no longer counts as shared.`)) return;
    setError(null);
    try {
      await deleteLiveAuctionGrant(g.id);
      setTouched(true);
      await fetchGrants();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={() => onClose(touched)}>
      <div className="w-full max-w-xl bg-bg-secondary border-l border-border-default h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border-default flex items-start justify-between">
          <div>
            <div className="text-text-muted text-[10px] uppercase tracking-wider font-semibold">Auction</div>
            <h2 className="text-text-primary text-lg font-bold mt-0.5">{template.name}</h2>
            {template.season && <div className="text-text-secondary text-xs">{template.season}</div>}
            <div className="text-text-secondary text-xs mt-2">
              {template.teams_count} teams · {template.sets_count} player sets · owner {template.owner?.full_name || template.owner?.email}
            </div>
          </div>
          <button className="text-text-secondary hover:text-text-primary" onClick={() => onClose(touched)} title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded px-3 py-2 text-xs flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-text-primary text-sm font-semibold">Grantees ({grants.length})</div>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-button-primary text-white rounded text-xs hover:opacity-90"
              >
                <Plus className="w-3.5 h-3.5" /> Add user
              </button>
            </div>

            {loading ? (
              <div className="text-text-secondary text-xs py-6 text-center">Loading…</div>
            ) : grants.length === 0 ? (
              <div className="rounded border border-dashed border-border-default p-6 text-center">
                <UsersIcon className="w-7 h-7 text-text-muted mx-auto mb-1.5" />
                <div className="text-text-secondary text-xs">Not shared with anyone yet.</div>
              </div>
            ) : (
              <ul className="space-y-1.5">
                {grants.map((g) => (
                  <li key={g.id} className="bg-bg-primary/40 border border-border-default rounded px-3 py-2 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-text-primary text-sm font-medium truncate">
                        {g.user_detail?.full_name || g.user_detail?.email}
                      </div>
                      <div className="text-text-secondary text-[10.5px] truncate">
                        {g.user_detail?.email} · granted {fmtDate(g.granted_at)} by {g.granted_by_detail?.full_name || g.granted_by_detail?.email}
                      </div>
                    </div>
                    <button
                      onClick={() => revoke(g)}
                      className="text-text-secondary hover:text-red-300 p-1.5"
                      title="Revoke grant"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="text-text-secondary text-[10.5px] leading-relaxed bg-bg-primary/30 rounded p-3 border border-border-default">
            <div className="font-semibold text-text-primary text-xs mb-1">How it works</div>
            On grant, the auction's full setup (teams, retentions, player sets, settings) is deep-copied into the grantee's analyzer. Their sale-log, unsold-log and "my team" pick stay scoped to their copy — multiple users can each get their own independent clone of the same template. Revoking removes the grant row only; the grantee's in-progress work isn't destroyed.
          </div>
        </div>

        {pickerOpen && (
          <UserPickerModal
            onPick={grant}
            onClose={() => setPickerOpen(false)}
            templateId={template.id}
            existingUserIds={grants.map((g) => g.user_detail?.id).filter(Boolean)}
          />
        )}
      </div>
    </div>
  );
}

function UserPickerModal({ onPick, onClose, templateId = "", existingUserIds = [] }) {
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Preload full list once; client-side filter after. Passing templateId
  // tells the backend to stamp each user with `has_grant_for_template`
  // so we can render the same emerald "granted" tag the analyzer picker
  // uses + `grants_total` for cross-template context.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    searchLiveAuctionGrantUsers("", "", templateId)
      .then((res) => {
        if (!alive) return;
        const list = res?.data?.users || [];
        setUsers(list);
      })
      .catch((err) => {
        if (!alive) return;
        const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Failed to load users.";
        setError(`${err?.response?.status ? `(${err.response.status}) ` : ""}${detail}`);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [templateId]);

  const filtered = useMemo(() => {
    let rows = users;
    if (seg) rows = rows.filter((u) => (u.account_type || "") === seg);
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((u) =>
      (u.email || "").toLowerCase().includes(t)
      || (u.username || "").toLowerCase().includes(t)
      || (u.full_name || "").toLowerCase().includes(t)
      || (u.organization_name || "").toLowerCase().includes(t)
    );
  }, [users, q, seg]);

  const counts = useMemo(() => {
    const c = { ALL: users.length, B2C: 0, B2B: 0, STAFF: 0 };
    for (const u of users) {
      const k = u.account_type || "B2C";
      if (k in c) c[k] += 1;
    }
    return c;
  }, [users]);

  const existing = new Set(existingUserIds);

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bg-secondary border border-border-default rounded-lg w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 border-b border-border-default flex items-center gap-2">
          <Search className="w-4 h-4 text-text-secondary shrink-0" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={loading ? "Loading users…" : `Filter ${users.length} users…`}
            className="flex-1 bg-transparent text-sm text-text-primary focus:outline-none"
          />
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-3 py-2 border-b border-border-default/60 flex flex-wrap items-center gap-1.5">
          <SegmentPill active={seg === ""}     onClick={() => setSeg("")}      label="All"   count={counts.ALL}   tone="slate" />
          <SegmentPill active={seg === "B2C"}   onClick={() => setSeg("B2C")}   label="B2C"   count={counts.B2C}   tone="emerald" />
          <SegmentPill active={seg === "B2B"}   onClick={() => setSeg("B2B")}   label="B2B"   count={counts.B2B}   tone="sky" />
          <SegmentPill active={seg === "STAFF"} onClick={() => setSeg("STAFF")} label="Staff" count={counts.STAFF} tone="indigo" />
          <span className="ml-auto text-[10px] uppercase tracking-wider text-text-secondary">
            {filtered.length} {filtered.length === 1 ? "match" : "matches"}
          </span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="m-3 bg-red-500/10 border border-red-500/30 text-red-300 rounded px-3 py-2 text-xs flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>Couldn't load users — {error}</span>
            </div>
          )}
          {loading ? (
            <div className="text-text-secondary text-xs py-6 text-center">Loading users…</div>
          ) : !error && filtered.length === 0 ? (
            <div className="text-text-secondary text-xs py-6 text-center">
              {users.length === 0 ? "No users in the system yet." : "No matches for the current filter."}
            </div>
          ) : !error ? (
            filtered.map((u) => {
              // "Already on this auction" is the local-list view (passed
              // in via existingUserIds) OR the server-stamped flag if
              // we passed templateId — either is authoritative.
              const grantedHere = existing.has(u.id) || !!u.has_grant_for_template;
              const otherGrants = Math.max(0, (u.grants_total || 0) - (grantedHere ? 1 : 0));
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => !grantedHere && onPick(u)}
                  disabled={grantedHere}
                  className={`w-full text-left px-4 py-2.5 border-b border-border-default/40 last:border-b-0 transition-colors ${
                    grantedHere
                      ? "bg-bg-primary/20 text-text-muted cursor-not-allowed"
                      : "hover:bg-bg-primary/40 text-text-primary"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{u.full_name || u.username}</div>
                      <div className="text-text-secondary text-xs truncate">
                        {u.email}
                        {u.organization_name && <span className="text-text-muted"> · {u.organization_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <AccountTypeTag type={u.account_type} />
                      {grantedHere && (
                        <span className="text-emerald-300 text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-400/30">granted</span>
                      )}
                      {!grantedHere && otherGrants > 0 && (
                        <span
                          className="text-text-secondary text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-bg-primary border border-border-default"
                          title={`This user has ${otherGrants} grant${otherGrants === 1 ? '' : 's'} for other auctions.`}
                        >
                          +{otherGrants} other
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ─── Pre-Auction Access section ─────────────────────────────────────
   Admin shares a Pre-Auction analysis context (the chat-style tool
   backed by feeder Competitions) with specific analysts. Visibility
   model is Auction.shared_with M2M — unlike LiveAuction which clones
   state per-grantee, Pre-Auction is a single shared context so the
   "grant" is just adding the user to the M2M. */

function PreAuctionAccessSection() {
  const [auctions, setAuctions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openAuctionId, setOpenAuctionId] = useState(null);

  const fetchAuctions = async () => {
    setLoading(true);
    try {
      const res = await listPreAuctions(search);
      setAuctions(res.data.pre_auctions || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchAuctions, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openAuction = auctions.find((a) => a.id === openAuctionId);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Pre-Auction access</h1>
          <p className="text-sm text-text-secondary mt-1">
            Share a Pre-Auction analysis context with a user. They can browse the linked feeder tournaments and use the chat surface to ask questions. Pre-Auctions are NOT per-user — every grantee sees the same shared context.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by Pre-Auction name…"
            className="w-full pl-9 pr-3 py-2 bg-bg-secondary border border-border-default rounded text-sm"
          />
        </div>
        <button onClick={fetchAuctions} className="text-text-secondary hover:text-text-primary" title="Refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
        <div className="text-xs text-text-secondary ml-auto">
          {auctions.length} Pre-Auction{auctions.length === 1 ? "" : "s"}
        </div>
      </div>

      {loading && auctions.length === 0 ? (
        <div className="bg-bg-secondary border border-border-default rounded-lg p-6 text-center text-text-secondary text-sm">Loading…</div>
      ) : auctions.length === 0 ? (
        <div className="bg-bg-secondary border border-border-default rounded-lg p-8 text-center">
          <ClipboardList className="w-10 h-10 text-text-secondary mx-auto mb-3" />
          <p className="text-text-primary font-semibold">No Pre-Auctions yet.</p>
          <p className="text-text-secondary text-sm mt-1">
            Create one inside the analyzer SPA (Manivannan's account) → Auction → Pre-Auction → "Create Auction".
          </p>
        </div>
      ) : (
        <PreAuctionsTable auctions={auctions} onPick={setOpenAuctionId} />
      )}

      {openAuction && (
        <PreAuctionGrantsDrawer
          auction={openAuction}
          onClose={(refresh) => {
            setOpenAuctionId(null);
            if (refresh) fetchAuctions();
          }}
        />
      )}
    </div>
  );
}

function PreAuctionsTable({ auctions, onPick }) {
  return (
    <div className="bg-bg-secondary border border-border-default rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-bg-primary/40 text-text-secondary text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-4 py-2.5 font-semibold">Pre-Auction</th>
            <th className="text-left px-4 py-2.5 font-semibold">Created by</th>
            <th className="text-right px-4 py-2.5 font-semibold">Feeders</th>
            <th className="text-right px-4 py-2.5 font-semibold">Granted to</th>
            <th className="text-left px-4 py-2.5 font-semibold">Updated</th>
            <th className="px-4 py-2.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-default">
          {auctions.map((a) => (
            <tr key={a.id} className="hover:bg-bg-primary/30 cursor-pointer" onClick={() => onPick(a.id)}>
              <td className="px-4 py-3">
                <div className="text-text-primary font-medium">{a.name}</div>
                {a.season && <div className="text-text-secondary text-xs">{a.season}</div>}
              </td>
              <td className="px-4 py-3 text-text-secondary text-xs">{a.created_by?.email || "—"}</td>
              <td className="px-4 py-3 text-right tabular-nums">{a.feeders_count}</td>
              <td className="px-4 py-3 text-right tabular-nums">
                <span className={a.grants_count > 0 ? "text-emerald-300 font-semibold" : "text-text-secondary"}>
                  {a.grants_count} {a.grants_count === 1 ? "user" : "users"}
                </span>
              </td>
              <td className="px-4 py-3 text-text-secondary text-xs">{fmtDate(a.updated_at)}</td>
              <td className="px-4 py-3 text-right">
                <button className="text-button-primary text-xs hover:underline">Manage →</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PreAuctionGrantsDrawer({ auction, onClose }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);

  const fetchGrants = async () => {
    setLoading(true);
    try {
      const res = await getPreAuctionGrants(auction.id);
      setGrants(res.data.grants || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGrants(); /* eslint-disable-next-line */ }, [auction.id]);

  const addGrant = async (user) => {
    setError(null);
    try {
      await addPreAuctionGrant({ auctionId: auction.id, userId: user.id });
      setTouched(true);
      setPickerOpen(false);
      await fetchGrants();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.detail || err.message);
    }
  };

  const revoke = async (u) => {
    if (!confirm(`Revoke ${u.full_name || u.email}'s access to this Pre-Auction?`)) return;
    setError(null);
    try {
      await removePreAuctionGrant({ auctionId: auction.id, userId: u.id });
      setTouched(true);
      await fetchGrants();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={() => onClose(touched)}>
      <div className="w-full max-w-xl bg-bg-secondary border-l border-border-default h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border-default flex items-start justify-between">
          <div>
            <div className="text-text-muted text-[10px] uppercase tracking-wider font-semibold">Pre-Auction</div>
            <h2 className="text-text-primary text-lg font-bold mt-0.5">{auction.name}</h2>
            {auction.season && <div className="text-text-secondary text-xs">{auction.season}</div>}
            <div className="text-text-secondary text-xs mt-2">
              {auction.feeders_count} feeder{auction.feeders_count === 1 ? "" : "s"} · created by {auction.created_by?.email || "—"}
            </div>
          </div>
          <button className="text-text-secondary hover:text-text-primary" onClick={() => onClose(touched)} title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded px-3 py-2 text-xs flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-text-primary text-sm font-semibold">Grantees ({grants.length})</div>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-button-primary text-white rounded text-xs hover:opacity-90"
              >
                <Plus className="w-3.5 h-3.5" /> Add user
              </button>
            </div>

            {loading ? (
              <div className="text-text-secondary text-xs py-6 text-center">Loading…</div>
            ) : grants.length === 0 ? (
              <div className="rounded border border-dashed border-border-default p-6 text-center">
                <UsersIcon className="w-7 h-7 text-text-muted mx-auto mb-1.5" />
                <div className="text-text-secondary text-xs">Not shared with anyone yet.</div>
              </div>
            ) : (
              <ul className="space-y-1.5">
                {grants.map((u) => (
                  <li key={u.id} className="bg-bg-primary/40 border border-border-default rounded px-3 py-2 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-text-primary text-sm font-medium truncate">
                        {u.full_name || u.email}
                      </div>
                      <div className="text-text-secondary text-[10.5px] truncate">
                        {u.email}
                        {u.organization_name && <span className="text-text-muted"> · {u.organization_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <AccountTypeTag type={u.account_type} />
                      <button
                        onClick={() => revoke(u)}
                        className="text-text-secondary hover:text-red-300 p-1.5"
                        title="Revoke access"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="text-text-secondary text-[10.5px] leading-relaxed bg-bg-primary/30 rounded p-3 border border-border-default">
            <div className="font-semibold text-text-primary text-xs mb-1">How it works</div>
            Adding a user gives them read access to this Pre-Auction's analysis context: the linked feeder competitions, the auction overview, and the chat surface for asking questions. Unlike LiveAuctions, Pre-Auctions are <strong>not cloned per grantee</strong> — every grantee sees the same shared context. Revoking removes their visibility on the next page load.
          </div>
        </div>

        {pickerOpen && (
          <PreAuctionUserPickerModal
            auctionId={auction.id}
            existingUserIds={grants.map((g) => g.id)}
            onPick={addGrant}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

function PreAuctionUserPickerModal({ auctionId, existingUserIds = [], onPick, onClose }) {
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    searchPreAuctionGrantUsers("", auctionId)
      .then((res) => {
        if (!alive) return;
        setUsers(res?.data?.users || []);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.response?.data?.detail || err?.message || "Failed to load users.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [auctionId]);

  const filtered = useMemo(() => {
    let rows = users;
    if (seg) rows = rows.filter((u) => (u.account_type || "") === seg);
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((u) =>
      (u.email || "").toLowerCase().includes(t)
      || (u.username || "").toLowerCase().includes(t)
      || (u.full_name || "").toLowerCase().includes(t)
      || (u.organization_name || "").toLowerCase().includes(t)
    );
  }, [users, q, seg]);

  const counts = useMemo(() => {
    const c = { ALL: users.length, B2C: 0, B2B: 0, STAFF: 0 };
    for (const u of users) {
      const k = u.account_type || "B2C";
      if (k in c) c[k] += 1;
    }
    return c;
  }, [users]);

  const existing = new Set(existingUserIds);

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bg-secondary border border-border-default rounded-lg w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 border-b border-border-default flex items-center gap-2">
          <Search className="w-4 h-4 text-text-secondary shrink-0" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={loading ? "Loading users…" : `Filter ${users.length} users…`}
            className="flex-1 bg-transparent text-sm text-text-primary focus:outline-none"
          />
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-3 py-2 border-b border-border-default/60 flex flex-wrap items-center gap-1.5">
          <SegmentPill active={seg === ""}     onClick={() => setSeg("")}      label="All"   count={counts.ALL}   tone="slate" />
          <SegmentPill active={seg === "B2C"}   onClick={() => setSeg("B2C")}   label="B2C"   count={counts.B2C}   tone="emerald" />
          <SegmentPill active={seg === "B2B"}   onClick={() => setSeg("B2B")}   label="B2B"   count={counts.B2B}   tone="sky" />
          <SegmentPill active={seg === "STAFF"} onClick={() => setSeg("STAFF")} label="Staff" count={counts.STAFF} tone="indigo" />
          <span className="ml-auto text-[10px] uppercase tracking-wider text-text-secondary">
            {filtered.length} {filtered.length === 1 ? "match" : "matches"}
          </span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="m-3 bg-red-500/10 border border-red-500/30 text-red-300 rounded px-3 py-2 text-xs flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>Couldn't load users — {error}</span>
            </div>
          )}
          {loading ? (
            <div className="text-text-secondary text-xs py-6 text-center">Loading users…</div>
          ) : !error && filtered.length === 0 ? (
            <div className="text-text-secondary text-xs py-6 text-center">
              {users.length === 0 ? "No users in the system yet." : "No matches for the current filter."}
            </div>
          ) : !error ? (
            filtered.map((u) => {
              const grantedHere = existing.has(u.id) || !!u.has_grant_for_template;
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => !grantedHere && onPick(u)}
                  disabled={grantedHere}
                  className={`w-full text-left px-4 py-2.5 border-b border-border-default/40 last:border-b-0 transition-colors ${
                    grantedHere
                      ? "bg-bg-primary/20 text-text-muted cursor-not-allowed"
                      : "hover:bg-bg-primary/40 text-text-primary"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{u.full_name || u.username}</div>
                      <div className="text-text-secondary text-xs truncate">
                        {u.email}
                        {u.organization_name && <span className="text-text-muted"> · {u.organization_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <AccountTypeTag type={u.account_type} />
                      {grantedHere && (
                        <span className="text-emerald-300 text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-400/30">granted</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          ) : null}
        </div>
      </div>
    </div>
  );
}
